// src/App.js
import React, { useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { Layout } from "./components/common/Layout";
import { Home } from "./pages/Home";
import { About } from "./pages/About";
import { Practice } from "./pages/Practice";
import Pricing from "./pages/Pricing";
import { WhyUs } from "./pages/WhyUs";
import Contact from "./pages/Contact";
import Login from "./pages/Login";
import Admin from "./pages/Admin";
import SubjectPapers from "./pages/SubjectPapers";
import Tuition from "./pages/Tuition";
import GetHelp from "./pages/GetHelp";
import PaperView from "./pages/PaperView";
import MCQBrowse from "./pages/MCQBrowse";
import MCQTest   from "./pages/MCQTest";
import StudentLogin from "./pages/StudentLogin";
import StudentDashboard from "./pages/StudentDashboard";
import StudentProgress from "./pages/StudentProgress";
import StudentProfile from "./pages/StudentProfile";
import StudentLibrary from "./pages/StudentLibrary";
import Certificate from "./pages/Certificate";
import Leaderboard from "./pages/Leaderboard";
import { useVisitorCount } from "./components/common/useVisitorCount";
import { useAuth } from "./context/AuthContext";
import Loader from "./components/common/Loader";
import StudentLayout from "./components/student/StudentLayout";
import SmartLayout from "./components/student/SmartLayout";
import { SEO } from "./components/common/SEO";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function ProtectedRoute({ children }) {
  const navigate = useNavigate();
  const accessToken = localStorage.getItem("accessToken");

  useEffect(() => {
    if (!accessToken) {
      navigate("/login");
    }
  }, [accessToken, navigate]);

  return accessToken ? children : null;
}

// Guards student-only pages. Waits for auth to resolve, then requires role==="student".
function StudentRoute({ children }) {
  const navigate = useNavigate();
  const { role, loading } = useAuth();

  useEffect(() => {
    if (!loading && role !== "student") {
      navigate("/signin");
    }
  }, [loading, role, navigate]);

  if (loading) return <Loader />;
  return role === "student" ? children : null;
}

function App() {
  useVisitorCount();

  return (
    <BrowserRouter>
      <ScrollToTop />
      <Routes>
        {/* Home & static pages */}
        <Route path="/"       element={<Layout><Home /></Layout>} />
        <Route path="/about"  element={<Layout><About /></Layout>} />
        <Route path="/why-us" element={<Layout><WhyUs /></Layout>} />
        <Route path="/pricing" element={<Layout><Pricing /></Layout>} />
        <Route path="/contact" element={<Layout><Contact /></Layout>} />

        {/* Authentication */}
        <Route path="/login" element={<Layout><Login /></Layout>} />
        <Route path="/signin" element={<Layout><StudentLogin /></Layout>} />

        {/* Student portal — app shell (sidebar), no marketing chrome */}
        <Route
          path="/dashboard"
          element={
            <StudentRoute>
              <StudentLayout>
                <StudentDashboard />
              </StudentLayout>
            </StudentRoute>
          }
        />
        <Route
          path="/leaderboard"
          element={
            <StudentRoute>
              <StudentLayout>
                <Leaderboard />
              </StudentLayout>
            </StudentRoute>
          }
        />
        <Route
          path="/progress"
          element={
            <StudentRoute>
              <StudentLayout>
                <StudentProgress />
              </StudentLayout>
            </StudentRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <StudentRoute>
              <StudentLayout>
                <StudentProfile />
              </StudentLayout>
            </StudentRoute>
          }
        />
        <Route
          path="/library"
          element={
            <StudentRoute>
              <StudentLayout>
                <StudentLibrary />
              </StudentLayout>
            </StudentRoute>
          }
        />

        {/* Printable achievement certificate — full-screen, students only */}
        <Route
          path="/certificate"
          element={
            <StudentRoute>
              <Certificate />
            </StudentRoute>
          }
        />

        <Route
          path="/admin"
          element={
            <Layout>
              <ProtectedRoute>
                <Admin />
              </ProtectedRoute>
            </Layout>
          }
        />

        {/* Tuition */}
        <Route path="/tuition" element={<Layout><Tuition /></Layout>} />

        {/* Student lead capture */}
        <Route path="/get-help" element={<Layout><GetHelp /></Layout>} />

        {/* Paper viewer (guests + students) */}
        <Route path="/paper/:id" element={<SmartLayout><PaperView /></SmartLayout>} />

        {/* MCQ Tests — browse uses smart chrome; taking a quiz is full-screen (focus mode) */}
        <Route path="/mcq"     element={<SmartLayout><MCQBrowse /></SmartLayout>} />
        <Route path="/mcq/:id" element={<MCQTest />} />

        {/* Resources by type/grade */}
        <Route
          path="/resources/:type/:grade/:subject"
          element={<SmartLayout><SubjectPapers /></SmartLayout>}
        />

        {/* Practice landing (all grades selector) */}
        <Route path="/practice" element={<SmartLayout><Practice /></SmartLayout>} />

        {/* Practice by grade */}
        <Route path="/practice/:grade" element={<SmartLayout><Practice /></SmartLayout>} />

        {/* Practice detail (old: no board) */}
        <Route path="/practice/:grade/:examKey" element={<SmartLayout><Practice /></SmartLayout>} />

        {/* Practice with board param (4 segments — no ambiguity) */}
        <Route path="/practice/:board/:grade/:examKey" element={<SmartLayout><Practice /></SmartLayout>} />

        {/* catch-all 404 */}
        <Route
          path="*"
          element={
            <Layout>
              <div className="text-center py-16 text-pink-600">
                <SEO title="Page not found" noIndex />
                <h1 className="text-2xl font-semibold">Oops! Page Not Found.</h1>
              </div>
            </Layout>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
